import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy } from '@angular/core';
import * as d3 from 'd3';
import { stackLayout } from 'src/util/stack-layout';

interface StackSeries {
  name: string;
  /** One value per category, in the same order as {@link TenantStackBarChartComponent.categories}. */
  values: number[];
  /** For the "Other" series only: the individual tenants folded into it (each aligned to the same
   *  categories), so hovering "Other" can break it down per tenant. Absent on a normal series. */
  members?: { name: string; values: number[] }[];
}

interface LegendItem {
  name: string;
  color: string;
}

/** One drawn segment of a bar, carrying its true (unscaled-for-display) value for the tooltip. */
interface Segment {
  x: number;
  y: number;
  width: number;
  height: number;
  key: string;
  category: string;
  value: number;
  colorIndex: number;
}

/**
 * A stacked bar chart of a metric per month, each bar split by tenant. The bar height is the platform
 * total (so it matches the headline figure) and each segment is one tenant's share, with the smallest
 * tenants folded into a single "Other" segment upstream so the legend stays short. Backs both storage
 * charts and the per-tenant usage charts.
 *
 * Every non-zero segment is laid out to at least a minimum pixel height (see {@link stackLayout}) so a
 * tiny tenant is still visible and hoverable; the bar still sums to the true total, and the tooltip
 * always shows each tenant's real figure rather than the adjusted pixel size.
 */
@Component({
  selector: 'tenant-stack-bar-chart',
  templateUrl: './tenant-stack-bar-chart.component.html',
  styleUrls: ['./tenant-stack-bar-chart.component.scss'],
  standalone: false
})
export class TenantStackBarChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** Month labels along the x-axis, in order. */
  @Input() categories: string[] = [];
  /** One series per tenant (plus "Other"), values aligned to {@link categories}. */
  @Input() series: StackSeries[] = [];
  @Input() chartId = 'tenantStack';
  @Input() unit = 'GB';
  /** Optional formatter for the values shown in tooltips. When set (e.g. for a byte metric) it renders
   *  each figure in its own human unit, so a tenant far smaller than the chart unit still reads sensibly
   *  instead of as "0 GB". When absent, values fall back to a plain number plus {@link unit}. */
  @Input() formatValue?: (value: number) => string;

  legend: LegendItem[] = [];

  // Eight distinct hues for the biggest tenants; "Other" always renders in a muted grey so it reads as
  // the remainder rather than as a tenant of its own.
  private static readonly palette = ['#439FD3', '#39D6D6', '#B77FFF', '#14AE5C', '#E0A32E', '#F2779A', '#8C9EFF', '#5B6BF5'];
  private static readonly otherColor = '#6b7280';
  private static readonly otherName = 'Other';

  // Smallest a non-zero segment is ever drawn, so even a tiny tenant stays visible and hoverable.
  private static readonly minSegmentPx = 6;

  private readonly chartHeight = 340;

  // A single tooltip pinned to the viewport (position: fixed) and reused across renders, so it floats
  // over the hovered segment at the cursor without ever reflowing the chart.
  private tooltip: d3.Selection<HTMLDivElement, unknown, null, undefined> | null = null;

  // Watches this chart's own container so the SVG is redrawn to the current width whenever the box
  // changes size — crucially when the dashboard grid reflows from one to two columns as sibling
  // charts finish loading, which shrinks this chart without firing a window resize.
  private resizeObserver: ResizeObserver | null = null;
  private renderScheduled = false;

  constructor(private hostElement: ElementRef) {}

  ngAfterViewInit(): void {
    this.render();
    this.observeContainerResize();
  }

  ngOnChanges(): void {
    this.render();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.tooltip?.remove();
    this.tooltip = null;
  }

  // Re-render on any resize of the chart's container, not just the window. The grid reflow that moves
  // this chart from a full-width to a half-width cell shrinks the container silently; without this the
  // SVG keeps a stale, too-wide size and overflows the page. ResizeObserver also covers window resize.
  private observeContainerResize(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const container = this.hostElement.nativeElement.querySelector(`#${this.chartId}`) as HTMLElement | null;
    if (!container) {
      return;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.scheduleRender());
    this.resizeObserver.observe(container);
  }

  // Coalesce bursts of resize callbacks into a single redraw on the next frame.
  private scheduleRender(): void {
    if (this.renderScheduled) {
      return;
    }
    this.renderScheduled = true;
    requestAnimationFrame(() => {
      this.renderScheduled = false;
      this.render();
    });
  }

  private colorFor(name: string, index: number): string {
    return name === TenantStackBarChartComponent.otherName
      ? TenantStackBarChartComponent.otherColor
      : TenantStackBarChartComponent.palette[index % TenantStackBarChartComponent.palette.length];
  }

  private render(): void {
    const container = this.hostElement.nativeElement.querySelector(`#${this.chartId}`) as HTMLElement | null;
    if (!container) {
      return;
    }

    d3.select(`#${this.chartId}svg`).remove();

    this.legend = this.series.map((s, i) => ({ name: s.name, color: this.colorFor(s.name, i) }));

    if (!this.categories.length || !this.series.length) {
      return;
    }

    const margin = { top: 16, right: 16, bottom: 28, left: 56 };
    const width = Math.max(container.clientWidth || 600, 240);
    const height = this.chartHeight;
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    // The true total per month drives the axis and each bar's overall height; the per-segment pixel
    // split is adjusted for a minimum height but still sums back to this.
    const totals = this.categories.map((_, i) =>
      this.series.reduce((sum, s) => sum + Math.max(0, s.values[i] ?? 0), 0));
    const maxTotal = d3.max(totals) || 0;

    const x = d3.scaleBand().domain(this.categories).range([0, innerW]).padding(0.25);
    const y = d3.scaleLinear().domain([0, maxTotal]).nice().range([innerH, 0]);

    const svg = d3
      .select(container)
      .append('svg')
      .attr('id', `${this.chartId}svg`)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const tooltip = this.getTooltip();

    // Build every segment with a minimum hoverable height, stacking bottom-up per month. The bar's top
    // still lands on y(total) because the segment heights sum to the bar's true pixel height.
    const segments: Segment[] = [];
    this.categories.forEach((category, i) => {
      const total = totals[i];
      if (total <= 0) {
        return;
      }

      const barPixelHeight = innerH - y(total);
      const values = this.series.map((s) => Math.max(0, s.values[i] ?? 0));
      const heights = stackLayout(values, barPixelHeight, TenantStackBarChartComponent.minSegmentPx);

      const barX = x(category) ?? 0; // category is always in the band domain
      let cursorY = innerH;
      this.series.forEach((s, seriesIndex) => {
        const segmentHeight = heights[seriesIndex];
        const top = cursorY - segmentHeight;
        if (segmentHeight > 0) {
          segments.push({
            x: barX,
            y: top,
            width: x.bandwidth(),
            height: segmentHeight,
            key: s.name,
            category,
            value: values[seriesIndex],
            colorIndex: seriesIndex,
          });
        }
        cursorY = top;
      });
    });

    svg
      .selectAll('rect.segment')
      .data(segments)
      .enter()
      .append('rect')
      .attr('class', 'segment')
      .attr('x', (d) => d.x)
      .attr('width', (d) => d.width)
      .attr('y', (d) => d.y)
      .attr('height', (d) => d.height)
      .attr('fill', (d) => this.colorFor(d.key, d.colorIndex))
      .style('cursor', 'pointer')
      .on('mousemove', (event: MouseEvent, d) => {
        tooltip
          .style('display', 'block')
          .html(this.segmentTooltipHtml(d.key, d.category, d.value))
          .style('left', `${event.clientX + 14}px`)
          .style('top', `${event.clientY + 14}px`);
      })
      .on('mouseleave', () => tooltip.style('display', 'none'));

    svg
      .append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickPadding(6))
      .call((g) => g.select('.domain').attr('stroke', '#4A4A4A'))
      .selectAll('text')
      .style('fill', '#999999')
      .style('font-size', '12px');

    svg
      .append('g')
      .call(
        d3
          .axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickPadding(8)
          .tickFormat((d) => (d as number).toLocaleString())
      )
      .call((g) => g.select('.domain').remove())
      .selectAll('text')
      .style('fill', '#999999')
      .style('font-size', '12px');
  }

  // Formats a tooltip figure: the chart's own formatter when given (so a byte metric reads in its own
  // human unit, e.g. MB for a tiny tenant), otherwise a plain number plus the chart unit.
  private formatOne(value: number): string {
    return this.formatValue ? this.formatValue(value) : `${value.toLocaleString()} ${this.unit}`;
  }

  // Tooltip for one hovered segment. A normal tenant shows its name and that month's figure; the
  // "Other" segment also lists the tenants folded into it for that month, biggest first, so the
  // remainder is not a black box. The "Other" header is the sum of those members (not the drawn,
  // rounded segment value) so the header always equals the rows beneath it, and members are carried at
  // full precision so a tenant far smaller than the chart unit is not rounded to zero and dropped.
  private segmentTooltipHtml(key: string, category: string, value: number): string {
    if (key !== TenantStackBarChartComponent.otherName) {
      return `<strong>${this.escapeHtml(key)}</strong><br>${this.escapeHtml(category)}: ${this.formatOne(value)}`;
    }

    const index = this.categories.indexOf(category);
    const other = this.series.find((s) => s.name === TenantStackBarChartComponent.otherName);
    const members = (other?.members ?? [])
      .map((m) => ({ name: m.name, value: m.values[index] ?? 0 }))
      .filter((m) => m.value > 0)
      .sort((a, b) => b.value - a.value);

    // Fall back to the drawn segment value only when there are no members to sum (e.g. a chart whose
    // "Other" carries no breakdown); otherwise the header is the members' true total.
    const headValue = members.length ? members.reduce((sum, m) => sum + m.value, 0) : value;
    const head = `<strong>${this.escapeHtml(TenantStackBarChartComponent.otherName)}</strong>`
      + `<br>${this.escapeHtml(category)}: ${this.formatOne(headValue)}`;
    if (!members.length) {
      return head;
    }

    // Keep the tooltip usable when "Other" holds many tenancies: list the biggest and roll the tail up.
    const maxRows = 12;
    const shown = members.slice(0, maxRows);
    const rest = members.slice(maxRows);

    const rows = shown
      .map((m) => `<div style="display:flex;justify-content:space-between;gap:16px">`
        + `<span>${this.escapeHtml(m.name)}</span>`
        + `<span>${this.formatOne(m.value)}</span></div>`)
      .join('');

    const restTotal = rest.reduce((sum, m) => sum + m.value, 0);
    const more = rest.length
      ? `<div style="opacity:0.7;margin-top:2px">+${rest.length} more (${this.formatOne(restTotal)})</div>`
      : '';

    return `${head}<div style="border-top:1px solid #ffffff33;margin:5px 0"></div>${rows}${more}`;
  }

  private escapeHtml(value: string): string {
    const replacements: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return (value ?? '').replace(/[&<>"']/g, (c) => replacements[c]);
  }

  private getTooltip() {
    if (!this.tooltip) {
      this.tooltip = d3
        .select(document.body)
        .append('div')
        .attr('class', 'tenant-stack-tooltip')
        .style('position', 'fixed')
        .style('display', 'none')
        .style('pointer-events', 'none')
        .style('z-index', '2000')
        .style('background', '#000000cc')
        .style('color', '#fff')
        .style('border', '1px solid #ccc')
        .style('border-radius', '5px')
        .style('padding', '5px 8px')
        .style('font-size', '12px')
        .style('line-height', '1.3')
        .style('white-space', 'nowrap')
        .style('box-shadow', '0 2px 5px rgba(0, 0, 0, 0.2)');
    }
    return this.tooltip;
  }
}
